import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { ProductSearchInput } from "./ProductSearchInput"
import { fetchProductLots } from "../../../../services/containerTemplateServices"

export const ContainerItemCard = ({
    item,
    index,
    storeId,
    isEditing,
    onStartEdit,
    onUpdate,
    onRemove,
    onDone,
    availableTemplates = [],
}) => {
    const { i18n } = useTranslation()

    const isSubContainer = !!item.child_template_id || item.type === 'sub_container'

    // Fetch lots when product has expiration
    const { data: lotsData } = useQuery(
        ['product-lots', item.product_id],
        () => fetchProductLots(item.product_id),
        {
            enabled: !!item.product_id && !!item.product?.has_expiration && isEditing,
            refetchOnWindowFocus: false,
        }
    )

    const lots = lotsData?.product_lots || lotsData || []
    const totalPieces = (item.quantity || 0) * (item.units_per_container || 0)

    const handleFieldChange = (field, value) => {
        onUpdate(index, { ...item, [field]: value })
    }

    const handleProductSelect = (product) => {
        onUpdate(index, {
            ...item,
            product_id: product?.id || null,
            product: product,
            lot_id: null,
        })
    }

    const handleTemplateSelect = (templateId) => {
        const template = availableTemplates.find(t => t.id === parseInt(templateId))
        onUpdate(index, {
            ...item,
            child_template_id: template?.id || null,
            child_template: template,
        })
    }

    // EDIT MODE
    if (isEditing) {
        if (isSubContainer) {
            return (
                <div className="border-2 border-purple-400 rounded-lg p-4 bg-white min-w-[200px] shadow-md">
                    <div className="text-xs font-bold text-purple-600 mb-3">
                        {i18n.t('container_templates.editor.add_sub_container')}
                    </div>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.template')}</label>
                            <select
                                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm mt-1"
                                value={item.child_template_id || ''}
                                onChange={(e) => handleTemplateSelect(e.target.value)}
                            >
                                <option value="">{i18n.t('container_templates.editor.select_template')}</option>
                                {availableTemplates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name || t.grouping_code}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.quantity')}</label>
                            <input
                                type="number"
                                min="1"
                                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm mt-1"
                                value={item.quantity || ''}
                                onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className="flex justify-between pt-2">
                            <button
                                className="text-xs text-red-600 hover:text-red-800"
                                onClick={() => onRemove(index)}
                            >
                                {i18n.t('container_templates.editor.remove')}
                            </button>
                            <button
                                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                onClick={() => onDone(index)}
                            >
                                {i18n.t('container_templates.editor.done')}
                            </button>
                        </div>
                    </div>
                </div>
            )
        }

        // Product item edit mode
        return (
            <div className="border-2 border-blue-400 rounded-lg p-4 bg-white min-w-[200px] shadow-md">
                <div className="text-xs font-bold text-blue-600 mb-3">
                    {i18n.t('container_templates.editor.product')}
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.product')}</label>
                        <ProductSearchInput
                            storeId={storeId}
                            value={item.product}
                            onChange={handleProductSelect}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.quantity')}</label>
                            <input
                                type="number"
                                min="1"
                                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm mt-1"
                                value={item.quantity || ''}
                                onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.units_per_container')}</label>
                            <input
                                type="number"
                                min="1"
                                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm mt-1"
                                value={item.units_per_container || ''}
                                onChange={(e) => handleFieldChange('units_per_container', parseInt(e.target.value) || 0)}
                            />
                        </div>
                    </div>
                    {item.product?.has_expiration && (
                        <div>
                            <label className="text-xs text-gray-600">{i18n.t('container_templates.editor.lot')}</label>
                            <select
                                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm mt-1"
                                value={item.lot_id || ''}
                                onChange={(e) => handleFieldChange('lot_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                            >
                                <option value="">{i18n.t('container_templates.editor.no_lot')}</option>
                                {(Array.isArray(lots) ? lots : []).map((lot) => (
                                    <option key={lot.id} value={lot.id}>{lot.name || lot.lot_number}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {totalPieces > 0 && (
                        <div className="text-sm font-bold text-blue-700 bg-blue-50 p-2 rounded text-center">
                            = {totalPieces.toLocaleString()} {i18n.t('container_templates.editor.pieces_short')}
                        </div>
                    )}
                    <div className="flex justify-between pt-2">
                        <button
                            className="text-xs text-red-600 hover:text-red-800"
                            onClick={() => onRemove(index)}
                        >
                            {i18n.t('container_templates.editor.remove')}
                        </button>
                        <button
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            onClick={() => onDone(index)}
                        >
                            {i18n.t('container_templates.editor.done')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // VIEW MODE — Sub-container card
    if (isSubContainer) {
        const templateName = item.child_template?.name || item.child_template?.grouping_code || '?'
        return (
            <div
                className="border-2 border-purple-300 rounded-lg p-3 bg-white w-fit relative cursor-pointer hover:shadow-md hover:border-purple-500 transition-all"
                onClick={() => onStartEdit(index)}
            >
                <div className="absolute -top-2 left-2 bg-purple-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                    {i18n.t('container_templates.editor.add_sub_container')}
                </div>
                <div className="mt-2 text-center">
                    <div className="font-bold text-purple-900 text-sm">{templateName}</div>
                    <div className="text-xl font-bold text-purple-600 my-1">&times;{item.quantity || 0}</div>
                </div>
            </div>
        )
    }

    // VIEW MODE — Product card
    const productName = item.product?.name || (item.product_id ? `ID: ${item.product_id}` : '?')
    const productSku = item.product?.product_codes?.[0]?.sku || item.product?.sku || null
    const productBarcode = item.product?.bar_code || item.product?.barcode || null
    const productRef = productBarcode || productSku || null
    return (
        <div
            className="border-2 border-blue-300 rounded-lg p-3 bg-white w-fit relative cursor-pointer hover:shadow-md hover:border-blue-500 transition-all"
            onClick={() => onStartEdit(index)}
        >
            <div className="absolute -top-2 left-2 bg-blue-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                {i18n.t('container_templates.container_types.PRODUCT_CONTAINER')}
            </div>
            <div className="mt-2 text-center">
                <div className="font-bold text-blue-900 text-sm">{productName}</div>
                {productRef && (
                    <div className="text-[10px] text-gray-400">{productRef}</div>
                )}
                <div className="text-xl font-bold text-blue-600 my-1">{i18n.t('container_templates.editor.boxes_count', { count: item.quantity || 0 })}</div>
                <div className="text-xs text-gray-500">
                    {i18n.t('container_templates.editor.pieces_each', { count: item.units_per_container || 0 })}
                </div>
                <div className="mt-1 bg-blue-50 p-1 rounded text-xs font-bold text-blue-800">
                    = {totalPieces.toLocaleString()} {i18n.t('container_templates.editor.pieces_short')}
                </div>
            </div>
        </div>
    )
}
